// frontend/src/components/chat/ConversationContextIndicator.js
// Component to display active conversation context with clear button

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ShowChartIcon from '@mui/icons-material/ShowChart';

const ConversationContextIndicator = ({ context, onClear, loading = false }) => {
  // Debug logging
  console.log('📘 [ContextIndicator] Render called with:', {
    hasContext: !!context,
    hasSummary: !!context?.summary,
    summaryKeys: context?.summary ? Object.keys(context.summary) : null,
    lastTemplate: context?.last_template,
    messageCount: context?.message_count
  });
  
  // Don't render if no context
  if (!context || !context.summary) {
    console.log('⚠️ [ContextIndicator] Not rendering - no context or summary');
    return null;
  }

  const { summary } = context;
  
  // Check if context was extracted
  const isExtracted = summary.extracted === true;
  
  // Build display elements
  const displayElements = [];
  
  // Time period
  if (summary.time_period?.value) {
    displayElements.push({
      icon: <CalendarTodayIcon fontSize="small" />,
      label: summary.time_period.value,
      tooltip: 'Time Period'
    });
  }
  
  // Active filters (channel, campaign, product, geo, device)
  const activeFilters = [];
  if (summary.filters) {
    if (summary.filters.channel) {
      activeFilters.push({ key: 'Channel', value: summary.filters.channel });
    }
    if (summary.filters.campaign) {
      activeFilters.push({ key: 'Campaign', value: summary.filters.campaign });
    }
    if (summary.filters.product) {
      activeFilters.push({ key: 'Product', value: summary.filters.product });
    }
    if (summary.filters.geo) {
      activeFilters.push({ key: 'Location', value: summary.filters.geo });
    }
    if (summary.filters.device) {
      activeFilters.push({ key: 'Device', value: summary.filters.device });
    }
    
    // Other filters
    if (summary.filters.other && Object.keys(summary.filters.other).length > 0) {
      Object.entries(summary.filters.other).forEach(([key, value]) => {
        if (value) {
          activeFilters.push({ key, value });
        }
      });
    }
  }
  
  if (activeFilters.length > 0) {
    activeFilters.forEach(filter => {
      displayElements.push({
        icon: <FilterListIcon fontSize="small" />,
        label: `${filter.key}: ${filter.value}`,
        tooltip: 'Active Filter'
      });
    });
  }
  
  // Metrics - only show if we have room (max 4 total elements)
  if (summary.metrics && summary.metrics.length > 0 && displayElements.length < 3) {
    displayElements.push({
      icon: <ShowChartIcon fontSize="small" />,
      label: summary.metrics.slice(0, 2).join(', ') + (summary.metrics.length > 2 ? '...' : ''),
      tooltip: `Metrics: ${summary.metrics.join(', ')}`
    });
  }
  
  // Aggregation - only if compact enough
  if (summary.aggregation && displayElements.length < 4) {
    displayElements.push({
      icon: <ShowChartIcon fontSize="small" />,
      label: summary.aggregation,
      tooltip: 'Aggregation Level'
    });
  }
  
  // If no elements to display, don't render
  if (displayElements.length === 0) {
    return null;
  }
  
  // Keep only the most important 4 elements to avoid clutter
  const displayedElements = displayElements.slice(0, 4);
  
  return (
    <Fade in timeout={500}>
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 1,
          backgroundColor: isExtracted ? 'rgba(25, 118, 210, 0.04)' : 'rgba(158, 158, 158, 0.04)',
          borderLeft: isExtracted ? '3px solid #1976d2' : '3px solid #9e9e9e',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.75
        }}
      >
        {/* Context icon */}
        <Tooltip title={isExtracted ? "AI-extracted context active" : "Context active"}>
          <HistoryIcon 
            sx={{ 
              color: isExtracted ? 'primary.main' : 'text.secondary',
              mr: 0.5 
            }} 
          />
        </Tooltip>
        
        {/* Label */}
        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5, fontSize: '0.7rem', fontWeight: 500 }}>
          Context:
        </Typography>
        
        {/* Context chips - limited to 4 most important */}
        {displayedElements.map((element, index) => (
          <Tooltip key={index} title={element.tooltip} arrow>
            <Chip
              icon={element.icon}
              label={element.label}
              size="small"
              variant="outlined"
              sx={{
                height: '22px',
                fontSize: '0.7rem',
                borderColor: isExtracted ? 'primary.main' : 'text.secondary',
                color: isExtracted ? 'primary.main' : 'text.secondary',
                '& .MuiChip-icon': {
                  color: isExtracted ? 'primary.main' : 'text.secondary',
                  fontSize: '0.9rem'
                },
                '& .MuiChip-label': {
                  px: 1
                }
              }}
            />
          </Tooltip>
        ))}
        
        {/* Intent badge (if extracted) - more subtle */}
        {isExtracted && summary.intent && (
          <Chip
            label={summary.intent.replace(/_/g, ' ')}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ 
              textTransform: 'capitalize', 
              ml: 'auto',
              height: '22px',
              fontSize: '0.65rem',
              fontWeight: 600
            }}
          />
        )}
        
        {/* Clear button */}
        {/* Clear button - more subtle */}
        <Tooltip title="Clear context and start fresh" arrow>
          <IconButton
            size="small"
            onClick={onClear}
            disabled={loading}
            sx={{
              ml: activeFilters.length > 0 && !isExtracted ? 'auto' : 0.5,
              width: 24,
              height: 24,
              '&:hover': { 
                backgroundColor: 'error.light', 
                color: 'error.contrastText',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s'
            }}
          >
            <CloseIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      </Paper>
    </Fade>
  );
};

export default ConversationContextIndicator;
